import Stack from '@mui/material/Stack'
import { useTheme } from '@mui/material'
import { ReactNode } from 'react'

type Props = {
  children: ReactNode
  align: 'top' | 'bottom'
  reverse?: boolean
}

/**
 * The menu on the right of the graph and of the map.
 *
 * ⚠⚠ ONE PANEL, NOT A BOX FOR EACH ITEM (Benoit, 2026-10-02, nobgp/app#449). Each item had its own
 * translucent box. On the graph that reads as nothing, because the page is one colour. On the map
 * it was eight boxes of eight widths over the tiles. Now the stack is one translucent panel with
 * the blurred page behind it, and an item is a row of it: its label, then its icon.
 *
 * ⚠ A stack with no item draws nothing (`:empty`), so the tray at the bottom is not an empty panel.
 */
export default function SideMenuStack({ children, align, reverse }: Props) {
  const { spacing, shape, palette, breakpoints } = useTheme()

  return (
    <Stack
      direction={reverse ? 'column-reverse' : 'column'}
      sx={{
        position: align === 'top' ? 'absolute' : 'fixed',
        // Each row is as wide as the panel, so the hover of a row is the whole row.
        alignItems: 'stretch',
        right: spacing(1),
        top: align === 'top' ? { xs: spacing(7), sm: spacing(2) } : undefined,
        bottom: align === 'bottom' ? spacing(2) : undefined,
        py: 0.5,
        borderRadius: shape.borderRadiusLarge + 'px',
        border: `1px solid ${palette.divider}`,
        backdropFilter: shape.backdropBlur,
        backgroundColor: palette.transparent.lighten,
        overflow: 'hidden',
        '&:empty': { display: 'none' },
        '& a': { px: { xs: 1, sm: 2 }, py: 1 },
        '& .MuiTypography-root': {
          textAlign: 'right',
          marginRight: spacing(2),
          // ⚠ On a phone the menu is a narrow bar of icons: with its labels the panel was almost
          // half of a 375 px screen, over the map. The label stays in the page for a screen
          // reader (it is clipped, not removed). The tray at the bottom keeps its labels: they
          // are the names of the open terminals.
          ...(align === 'top'
            ? {
                [breakpoints.down('sm')]: {
                  position: 'absolute',
                  width: 1,
                  height: 1,
                  margin: -1,
                  overflow: 'hidden',
                  clip: 'rect(0 0 0 0)',
                  whiteSpace: 'nowrap',
                },
              }
            : {}),
        },

        '& .MuiButton-root': {
          justifyContent: 'flex-end',
          minWidth: 0,
          [breakpoints.down('sm')]: { paddingLeft: spacing(1), paddingRight: spacing(1) },
          borderRadius: 0,
          backgroundColor: 'transparent',
          '&:hover': { backgroundColor: palette.action.hover },
        },
      }}>
      {children}
    </Stack>
  )
}
